import { Box, makeStyles, Theme } from '@material-ui/core'
import { FormattedMessage, useIntl } from 'react-intl'
import { Field, FieldProps, Form, Formik, FormikProps } from 'formik'
import { Button } from '@src/stories/Buttons/Button'
import LiquidSelectAdapter from '@src/components/LiquidSelectAdapter/LiquidSelectAdapter'
import LiquidInputAdapter from '@src/components/LiquidInputAdapter'
import PartnerAddressSearchFields from './PartnerAddressSearchFields'
import {
  MaterialsDirectInput,
  MaterialsDirectStatusType,
} from '@src/types/graphql-types'
import { Message } from '@src/utils/messages'
import { useMemo } from 'react'

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    paddingBottom: theme.spacing(5),
    position: 'relative',
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      maxWidth: '50%',
    },
  },
  orderInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    marginBottom: theme.spacing(5),
    alignItems: 'flex-end',
    '& div': {
      width: '100%',
    },

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',

      '& div': {
        flexGrow: 1,
        flexShrink: 1,
        flexBasis: 0,
      },
    },
  },
  actionContainer: {
    position: 'relative',
  },
  searchButton: {
    minWidth: '100%',
  },
  clearButton: {
    position: 'absolute',
    top: 38,
    right: -28,
    fontSize: theme.typography.pxToRem(14),
  },
}))

interface MaterialsDirectOptionStatusType {
  value: MaterialsDirectStatusType | 'ALL'
  label: Message
}

interface MDSearchFormProps {
  initialValues: any
  handleSubmit: (values: MaterialsDirectInput) => void
  clearSearch: () => void
}

const MDSearchForm = ({
  initialValues,
  handleSubmit,
  clearSearch,
}: MDSearchFormProps) => {
  const classes = useStyles()
  const intl = useIntl()

  const materialsDirectStatusOptions: MaterialsDirectOptionStatusType[] = [
    {
      label: {
        id: 'ALL',
        defaultMessage: 'All',
      },
      value: 'ALL',
    },
    {
      label: {
        id: 'CANCELLED',
        defaultMessage: 'Cancelled',
      },
      value: MaterialsDirectStatusType.Cancelled,
    },
    {
      label: {
        id: 'INVOICED',
        defaultMessage: 'Invoiced',
      },
      value: MaterialsDirectStatusType.Invoiced,
    },
    {
      label: {
        id: 'OPEN',
        defaultMessage: 'Open',
      },
      value: MaterialsDirectStatusType.Open,
    },
    {
      label: {
        id: 'SHIPPED',
        defaultMessage: 'Shipped',
      },
      value: MaterialsDirectStatusType.Shipped,
    },
  ]

  const options = useMemo(() => {
    return materialsDirectStatusOptions.map((option) => ({
      ...option,
      label: intl.formatMessage(option.label),
    }))
  }, [intl])

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {(formikBag: FormikProps<MaterialsDirectInput>) => {
        return (
          <Form className={classes.form}>
            <Box className={classes.userInfo}>
              <PartnerAddressSearchFields formikBag={formikBag} />
            </Box>

            <Box className={classes.orderInfo}>
              <Field
                name="status"
                component={LiquidSelectAdapter}
                label={<FormattedMessage id="Status" defaultMessage="Status" />}
                options={options}
              />

              <Field
                name="orderNo"
                component={LiquidInputAdapter}
                label={
                  <FormattedMessage
                    id="ORDER_NUMBER"
                    defaultMessage="Order Number"
                  />
                }
                data-testid="order-number-input"
              />

              <Field
                name="poNo"
                component={LiquidInputAdapter}
                label={
                  <FormattedMessage id="PO_NUMBER" defaultMessage="PO Number" />
                }
                data-testid="po-number-input"
              />

              <Field
                name="customerMaterialNo"
                component={LiquidInputAdapter}
                label={
                  <FormattedMessage
                    id="CUSTOMER_PART"
                    defaultMessage="Customer Part"
                  />
                }
                data-testid="customer-part-input"
              />

              <Field
                name="lotNo"
                component={LiquidInputAdapter}
                label={
                  <FormattedMessage
                    id="LOT_NUMBER"
                    defaultMessage="Lot Number"
                  />
                }
                data-testid="lot-number-input"
              />

              <Field
                name="startDate"
                label={<FormattedMessage id="FROM" defaultMessage="From" />}
                data-testid="start-date-input"
                component={(props: FieldProps) => (
                  <LiquidInputAdapter {...props} type="date" />
                )}
              />

              <Field
                name="endDate"
                label={<FormattedMessage id="TO" defaultMessage="To" />}
                data-testid="end-date-input"
                component={(props: FieldProps) => (
                  <LiquidInputAdapter {...props} type="date" />
                )}
              />

              <Box className={classes.actionContainer}>
                <Button
                  data-testid="submit-btn"
                  variant="contained"
                  color="primary"
                  type="submit"
                  size="medium"
                  className={classes.searchButton}
                >
                  <FormattedMessage id="SEARCH" defaultMessage="Search" />
                </Button>
                <Button
                  data-testid="submit-btn"
                  variant="text"
                  color="primary"
                  type="submit"
                  size="medium"
                  className={classes.clearButton}
                  onClick={(e) => {
                    e.preventDefault()
                    formikBag.resetForm()
                    clearSearch()
                  }}
                >
                  <FormattedMessage
                    id="CLEAR_FILTERS"
                    defaultMessage="Clear Filters"
                  />
                </Button>
              </Box>
            </Box>
          </Form>
        )
      }}
    </Formik>
  )
}

export default MDSearchForm
